/**
 * Slicer — the shared left filter rail (`KLQ-20260824-001`), generalized from
 * `features/tasks/HierarchyFilter.tsx` (which it has since replaced outright — the bar form
 * was deleted on 2026-08-25 when the rail moved above all three task arrangements) and
 * modelled on ayon-frontend's
 * `shared/src/containers/Slicer`.
 *
 * **Three gestures, three meanings, and none of them borrows another's** (owner decision #86,
 * 2026-09-15, option 3; `KLQ-20260820-005` §5). Until that day a row had one gesture with four
 * chords — plain click replaced, `Cmd`/`Ctrl` toggled, `Shift` took a range, both added the
 * range — and whatever those chords built *was* the slice, so "the rows I have picked" and
 * "what every list on the screen answers about" were one list under one name. The decision
 * splits them by *where on the row the pointer is*, which is the split the tree table already
 * makes (`KLQ-20260825-006` §B.8):
 *
 * | where | what it does | what it costs |
 * |---|---|---|
 * | the chevron | opens or shuts the branch | nothing — no surface re-queries |
 * | the select column | ticks the row into the rail's selection | nothing — no surface re-queries |
 * | the label | slices to that row | the sliced lists refetch |
 *
 * **A label click replaces the slice; `Cmd`/`Ctrl` + click adds or removes one row from it.**
 * That is §5's *"`Cmd`-click adds"*, kept, and it is the only chord the label still has. `Shift`
 * on a label means nothing any more — a range is a way of *building a set*, and building a set
 * is what the select column is for — so a `Shift` + click on a label is an ordinary click.
 *
 * **Clicking the label of the row that is the whole slice clears it.** There is no *everything*
 * row — it was removed on 2026-08-25 — and a rail that can narrow but not widen strands whoever
 * pressed it. The escape survived the decision because it never belonged to the selection
 * model: it is the slice's own *"never mind"*, and the table's `press` keeps the same rule for
 * the same reason. Every page also draws the slice as removable chips with *Show everything*
 * beside the list; this is the in-rail half of that, not a replacement for it.
 *
 * **A slice of several subtrees still exists, and the selection is how a person composes one
 * without re-slicing on every tick.** `KLQ-20260825-001` §9 made *Block A and Block B* one
 * question with two answers, and `under=` is a list the server unions. Two ways reach it:
 * `Cmd`-clicking labels (each press re-slices — immediate, one row at a time), or ticking rows
 * and pressing *Show only these* in the strip that appears under the list (one slice, one
 * refetch, then the ticks clear because they have become the slice). **Selection and slice stay
 * two lists**: a tick is a draft nobody else can see, the slice is the URL, and the only thing
 * that turns one into the other is a button that says so. That is the line the decision exists
 * to draw — a tick that re-sliced would be the old conflation behind a checkbox.
 *
 * **The selection is `tree-table/selection.tsx`, not a second model** (retro rule 8). The rail
 * registers its drawn rows as a one-column grid — `SELECT_COLUMN_KEY` and nothing else — and
 * gets §B.8's dialect for free: a tick toggles, `Shift` adds the interval from the anchor, a drag
 * down the column paints, `Escape` clears. It mounts **its own** `SelectionProvider`, because
 * the rail's ticks are not the table's: the tasks and register screens put a provider above
 * their table, and a rail that shared it would put hierarchy nodes into a set a bulk *Close*
 * reads task ids out of. Two consequences are inherited on purpose rather than patched here:
 * a ticked row hidden by a fold or a search leaves the count until it is drawn again (§B.4,
 * "rendered order is the only order"), and there is no select-all header — ticking every node
 * of a tree and slicing to it is the unsliced list with extra steps.
 *
 * **Its strip is not `SelectionBar`.** That bar floats over the bottom of the screen and belongs
 * to the table; the register and task screens already draw one there, and a second bar in the
 * same place would be two selections competing for one spot. The rail's strip lives inside the
 * rail, under the rows it counts.
 *
 * **ARIA says the same split.** `aria-selected` on a row means *ticked* — the tree is
 * `aria-multiselectable` because the select column makes it so — and the sliced rows carry
 * `aria-current`, which is what a slice is: where the screen currently is, not something picked.
 * A rail mounted with `selectable={false}` (the project-shape editor, which acts on one level)
 * draws no column, is not multiselectable and publishes no `aria-selected` at all.
 *
 * **One question at a time, several answers within it.** A facet is *what* is being sliced by
 * (a location, a person, a label); its options are the answers, nested when the facet is
 * itself a tree. The rail offers **one facet** at a time and as many options within it as the
 * person slices to. What stays refused is a rail holding *two subtrees and three people at
 * once*: that is a query builder, and a query builder above a list is the screen this
 * primitive exists instead of.
 *
 * **It narrows and never widens** — the rule `HierarchyFilter` was written under, kept
 * verbatim. The options offered are whatever the consumer passes, which for a scoped member
 * is an already-pruned tree; nothing here decides what may be reached, and a slice this rail
 * cannot name is still shown as sliced (`unknownLabel`), because a slice can outlive the branch
 * it names and dropping it silently would change what a shared link means.
 *
 * **The rows are a flat, windowed list** (`KLQ-20260820-005` §"A virtualised flat list", built
 * 2026-09-10). The tree is flattened once, depth becomes indentation, and only the rows the box
 * can show are in the document — a project at the 2 000-node cap draws a few dozen `<li>`, not
 * two thousand. `lib/windowing.ts` holds the arithmetic; the row height is pinned in CSS and its
 * twin is `ROW_HEIGHT` below (retro rule 12).
 *
 * **So the rail is one tab stop, not one per row**, and it has to be: a windowed list cannot be
 * tabbed through, because the row you would tab to is not in the document until it is scrolled
 * to. That is the WAI-ARIA tree pattern. DOM focus stays on the `<ul>`, the active row is
 * published as `aria-activedescendant`, and the window always draws it whatever the scroll
 * offset says. Arrow keys move it, `Home`/`End` jump, and the two activation keys are split
 * the way the pointer is: **`Enter` slices** (`Cmd`/`Ctrl` + `Enter` adds or removes), **`Space`
 * ticks** (`Shift` + `Space` adds the interval) — `Space` is the tree pattern's selection key
 * and the table's tick key, `Enter` is the default action. Without a select column `Space`
 * falls back to slicing, so a picker rail has both keys it had before.
 *
 * **Branches fold, with the tree table's own twisty** (owner, 2026-09-10: *"the slicer should
 * have the same chevron for expand/collapse as the tree table"*). Three things are shared rather
 * than reimplemented — the control (`components/Twisty`), what starts open
 * (`collapsedByDefault`, `openToDepth: 1`) and the words (`treeTable.expand` /
 * `treeTable.collapse`) — because a rail that folded differently from the table beside it would
 * be two instruments, not one.
 *
 * **A search overrides the fold.** `matching` keeps a match's ancestors as the path to it, and
 * leaving those ancestors shut would draw the path and hide every row the person was looking
 * for. While there is a needle nothing is shut; clearing it puts the folds back, because the
 * override is a derivation and never a write.
 *
 * **It knows nothing about tasks, drawings or any other domain.** Every word a person reads
 * that is *about* the data — the rail's title, each facet's name, the empty sentence — arrives
 * as a prop. What belongs to the control itself — the search box, the facet picker's label,
 * the sentence for a needle that matched nothing, and the select column's words — comes from
 * the `app` namespace, where the application chrome's own copy already lives.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";

import { ListFilter, Search } from "lucide-react";

import { Twisty, TwistySpacer } from "@/components/Twisty";
import { SELECT_COLUMN_KEY } from "@/components/tree-table/cell";
// The tree table's own default-fold rule, not a second copy of it: what starts open in the
// rail has to be what starts open in the table (`components/tree-table/lines.ts`).
import { collapsedByDefault } from "@/components/tree-table/lines";
// The table's selection model, not a second one — see the module note.
import {
  SelectionProvider,
  useSelection,
} from "@/components/tree-table/selection";

import { useMessages } from "@/lib/i18n";
import { CARDS, useMediaQuery } from "@/lib/media";
import { scrollRowIntoView, useListWindow } from "@/lib/windowing";

import { Button } from "../Button";
import { Drawer } from "../Drawer";
import { Select } from "../Select";

import styles from "./Slicer.module.css";

/**
 * One row's height in pixels.
 *
 * TWIN: `--slicer-row-height` in `Slicer.module.css`, which is what actually makes a row this
 * tall. The window's arithmetic is `index × height`, so a row that is not the height claimed
 * here scrolls to the wrong place — change one and change the other.
 */
const ROW_HEIGHT = 28;

/**
 * The rail's grid, as the selection sees it: the select column and nothing else. Module-level
 * so `registerGrid` is handed the same array every render and its equality check stays cheap.
 */
const TICK_COLUMNS: readonly string[] = [SELECT_COLUMN_KEY];

export interface SlicerOption {
  readonly id: string;
  readonly label: string;
  readonly children?: readonly SlicerOption[];
}

export interface SlicerFacet {
  readonly id: string;
  readonly label: string;
  readonly options: readonly SlicerOption[];
  /** Printed under the rows when this facet has nothing at all to offer. */
  readonly empty: string;
  /** How a slice the facet cannot name is printed. See the module note. */
  readonly unknownLabel: string;
}

export interface SlicerProps {
  /**
   * The rail's own heading, its accessible name, and the axis word in the phone button's.
   *
   * **It is *Os* · *Achse* · *Axis* since 2026-09-21**, and it is the same word on every screen
   * — but it arrives as a prop, so it is `slicer.title` in the `tasks`, `drawings`, `register`
   * and `reviews` namespaces, three locales each. Twelve keys, deliberately: this component may
   * not own copy (see the module note), and the alternative is a screen quietly disagreeing
   * with its neighbour about what the panel is called. **Renaming it is those twelve keys**, and
   * this is the pointer retro rule 12 asks for. It was *Výber / Auswahl / Slice* until that
   * date, which collided with the word for ticked rows in the same panel.
   */
  readonly title: string;
  readonly facets: readonly SlicerFacet[];
  readonly facetId: string;
  readonly onFacetChange: (facetId: string) => void;
  /** What the facet is sliced to. Empty is its whole range. */
  readonly sliced: readonly string[];
  /**
   * The **whole** next slice, never a delta. Called by a label click, `Enter`, the
   * *Show only these* strip and the unnameable-slice row — **never by a tick or a fold**, which
   * is the criterion this contract exists to keep (`KLQ-20260820-005`: expanding or selecting
   * issues no query). A consumer writes what it is handed and never re-derives what a press
   * meant.
   */
  readonly onSlice: (optionIds: readonly string[]) => void;
  /**
   * Whether the rail draws the select column. Default `true`, which is every filter rail.
   * `false` for a rail that is a picker for one thing — there is no set to build, so a column
   * for building one would be a control with nothing behind it.
   */
  readonly selectable?: boolean;
}

/** Every option, flattened, with its depth carried — what a `<select>` renders from. */
export function flattenOptions(
  options: readonly SlicerOption[],
  depth = 0,
): readonly {
  readonly id: string;
  readonly label: string;
  readonly depth: number;
}[] {
  return options.flatMap((option) => [
    { id: option.id, label: option.label, depth },
    ...flattenOptions(option.children ?? [], depth + 1),
  ]);
}

/** One drawn row: the option, its depth, and whether it has a branch and holds it open. */
interface SlicerRow {
  readonly id: string;
  readonly label: string;
  readonly depth: number;
  readonly expandable: boolean;
  readonly open: boolean;
}

/**
 * The rows actually drawn — **a shut branch's children are not among them** (owner,
 * 2026-09-10).
 *
 * The rail drew the whole tree flat until this, indentation and nothing else, so a project
 * with eight floors under two buildings was ten rows a person had to scroll past to reach the
 * next building. `TreeTable` has had a twisty since it existed; this is the same walk, the
 * same default (`collapsedByDefault`) and the same control (`components/Twisty`).
 *
 * `expandable` is inferred from the children the option has, and not declared: unlike the tree
 * table, a facet's options arrive whole — the rail never fetches a branch on opening it — so
 * there is no such thing here as a node whose children exist but have not been asked for.
 */
function visibleRows(
  options: readonly SlicerOption[],
  shut: ReadonlySet<string>,
  depth = 0,
): readonly SlicerRow[] {
  return options.flatMap((option) => {
    const children = option.children ?? [];
    const expandable = children.length > 0;
    const open = expandable && !shut.has(option.id);
    return [
      { id: option.id, label: option.label, depth, expandable, open },
      ...(open ? visibleRows(children, shut, depth + 1) : []),
    ];
  });
}

/**
 * The tree, narrowed to what matches — **ancestors kept**.
 *
 * A match dropped out of its branch is a floor with no building above it, which reads as a
 * different project rather than as a filtered one. An ancestor that does not match itself is
 * kept as the path to the ones that do.
 */
function matching(
  options: readonly SlicerOption[],
  needle: string,
): readonly SlicerOption[] {
  if (needle === "") return options;
  const out: SlicerOption[] = [];
  for (const option of options) {
    const children = matching(option.children ?? [], needle);
    if (
      option.label.toLocaleLowerCase().includes(needle) ||
      children.length > 0
    ) {
      out.push({ id: option.id, label: option.label, children });
    }
  }
  return out;
}

function has(options: readonly SlicerOption[], id: string): boolean {
  return options.some(
    (option) => option.id === id || has(option.children ?? [], id),
  );
}

/**
 * The rail, with a selection of its own mounted above it — **or, on a phone, a chip that opens
 * it in a `Drawer`.**
 *
 * The provider is here and not in the consumer so that no screen can hand the rail the
 * table's selection by accident — see the module note for what that would put in a bulk
 * action. With `selectable={false}` the provider is still mounted and simply never written.
 * It is also *above* the phone branch on purpose: the chip needs to clear the ticks when the
 * drawer is dismissed, and a provider inside the drawer would unmount with it.
 *
 * **Under 600px the rail is not drawn** (`KLQ-20260905-002` §C, owner default 2026-09-05,
 * confirmed 2026-09-21). `Screen` stacks it above the list at 900px with `max-height: 60dvh`,
 * which is right for a tablet and wrong for a phone: at 390px it is most of a screenful of
 * filter standing between a person and the list they opened the page for. So at the width the
 * table itself gives up and becomes cards, the rail gives up and becomes one chip.
 *
 * **A `Drawer`, not a new bottom sheet.** `FilterBar` already answers exactly this question
 * the same way — a popover on a pointer, a `Drawer` when stacked — and `STYLE.md` says a new
 * visual pattern is added to the shared components first and used second, never drawn locally
 * and generalised later. A second panel primitive whose only difference is which edge it comes
 * from would be that.
 */
export function Slicer(props: SlicerProps) {
  const phone = useMediaQuery(CARDS);
  return (
    <SelectionProvider>
      {phone ? <SlicerChip {...props} /> : <SlicerRail {...props} />}
    </SelectionProvider>
  );
}

/**
 * The phone form: one small icon button where the rail was, and the whole rail behind it.
 *
 * **It is a trigger, not a label** (owner, 2026-09-21 — *"daj to ako ikonku… tak ako mám
 * spravený filter nech je to malé tlačidlo"*). It shipped on 2026-09-21 morning as a chip
 * spelling the slice out — *"Structure: SO 01"* — on the theory that the chip should answer
 * *why is this list short* without being opened. It should; it is just not this control's job,
 * because **every screen that mounts a `Slicer` already draws the slice as removable chips in
 * its own toolbar** (see the module note). So the sentence was the second copy, it was the wide
 * one, and it was the copy standing on the row a phone has least of. What is left is the icon,
 * the count when something is sliced, and the same sentence as the accessible name — so a
 * reader who cannot see the toolbar chips still hears which axis this opens and where it is
 * standing.
 *
 * Geometry is `FilterBar`'s trigger exactly: 32px, same border, same radius, and it sits in the
 * same toolbar row (`components/screen` puts the rail there under 600px). Two controls that
 * open two panels over one list are two of the same thing and may not be two shapes.
 *
 * **Picking closes it.** A label click on a phone means *show me that*, and the panel has
 * finished its job the moment it is answered; the same goes for *Show only these*. What does
 * **not** close it is a tick or a fold — those build a draft, and closing the panel on the
 * first tick would make the several-row slice unreachable on the device that needs it most.
 */
function SlicerChip(props: SlicerProps) {
  const t = useMessages("app");
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const selection = useSelection();

  const facet = props.facets.find((one) => one.id === props.facetId) ?? props.facets[0];
  const sliced = props.sliced;
  const name =
    facet === undefined || sliced.length !== 1
      ? undefined
      : flattenOptions(facet.options).find((option) => option.id === sliced[0])?.label;

  // The axis, because with several facets the chip has to say *which* question it is short on.
  // With one facet the rail's own title is that name already.
  const axis = props.facets.length > 1 && facet !== undefined ? facet.label : props.title;

  const label =
    sliced.length === 0
      ? t("slicer.open", { axis })
      : name !== undefined
        ? t("slicer.openOne", { axis, name })
        : t("slicer.openMany", { axis, count: sliced.length });

  const close = (): void => {
    setOpen(false);
    // The ticks are a draft that only the open panel can see. Carrying them behind a closed
    // drawer would leave a count nobody can reach and a *Show only these* that acts on rows
    // the person stopped looking at two screens ago.
    selection.clear();
    trigger.current?.focus();
  };

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={[styles.chip, sliced.length > 0 ? styles.chipOn : ""]
          .filter(Boolean)
          .join(" ")}
        aria-expanded={open}
        // The whole sentence — *"Structure: SO 01"* — is the name even though only the icon is
        // drawn. `title` puts it back for a pointer; a phone has no hover and does not need it.
        aria-label={label}
        title={label}
        onClick={() => {
          setOpen((was) => !was);
        }}
      >
        <ListFilter size={14} strokeWidth={1.5} aria-hidden />
        {/* The number of sliced rows, not of axes: this control has one axis. It is decoration
            for the eye, and `aria-label` above already says the same thing in words.
            TWIN: `components/filter-bar/FilterBar.module.css`'s `.count`. */}
        {sliced.length === 0 ? null : (
          <span className={styles.chipCount} aria-hidden>
            {sliced.length}
          </span>
        )}
      </button>

      {!open ? null : (
        <Drawer title={props.title} closeLabel={t("slicer.close")} onClose={close}>
          <SlicerRail
            {...props}
            inDrawer
            onSlice={(optionIds) => {
              props.onSlice(optionIds);
              close();
            }}
          />
        </Drawer>
      )}
    </>
  );
}

function SlicerRail({
  title,
  facets,
  facetId,
  onFacetChange,
  sliced,
  onSlice,
  selectable = true,
  inDrawer = false,
}: SlicerProps & {
  /**
   * Drawn inside a `Drawer` rather than as the screen's rail — the phone branch above.
   *
   * Not part of `SlicerProps`: no consumer chooses this, `Slicer` does, and a public prop
   * would let a screen put the rail in a panel of its own and get the phone geometry on a
   * desktop. It carries only geometry — the rail in a drawer has no border, no sticky
   * position and no height cap of its own, because the panel already provides all three.
   */
  readonly inDrawer?: boolean;
}) {
  const t = useMessages("app");
  const selection = useSelection();
  const [search, setSearch] = useState("");

  const facet = facets.find((one) => one.id === facetId) ?? facets[0] ?? null;
  const needle = search.trim().toLocaleLowerCase();
  const options = useMemo(
    () => (facet === null ? [] : matching(facet.options, needle)),
    [facet, needle],
  );
  const slicedSet = useMemo(() => new Set(sliced), [sliced]);
  /**
   * Which branches are shut — **`null` until somebody presses a twisty**, exactly as
   * `TreeTable` holds it and for the same reason: the options arrive after the first render,
   * so a seeded `useState` would close nothing, and an effect re-seeding on every change would
   * throw away what the reader had opened. `null` means "nobody has said", and the default is
   * a derivation that follows the data in.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string> | null>(null);
  const byDefault = useMemo(
    () => (facet === null ? new Set<string>() : collapsedByDefault(facet.options, 1)),
    [facet],
  );
  /**
   * **A search overrides the fold.** `matching` already keeps a match's ancestors as the path
   * to it; leaving those ancestors shut would show the path and hide every row the person was
   * searching for, which reads as "no results" over a list that has them. So while there is a
   * needle, nothing is shut — and the moment it is cleared the folds are back as they were,
   * because this is a derivation and not a mutation of the set.
   */
  // Memoised, and the warning that asked for it is right: a fresh `new Set()` every render
  // would rebuild the whole row list on every keystroke elsewhere in the rail.
  const shut = useMemo(
    () => (needle === "" ? (collapsed ?? byDefault) : new Set<string>()),
    [needle, collapsed, byDefault],
  );
  /**
   * **The rows, and there is only one list of them.**
   *
   * The *filtered*, *folded* tree flattened, top to bottom, depth carried. It is what the window
   * draws, what the selection counts a `Shift` interval over — so an interval never reaches
   * through a row the search or a fold has hidden — and what the arrow keys step through. One
   * derivation for all three, because a second one is how an interval and a keypress start
   * disagreeing about what is above what.
   */
  const rows = useMemo(() => visibleRows(options, shut), [options, shut]);
  const order = useMemo(() => rows.map((one) => one.id), [rows]);

  // The selection is told what the rail drew, the way `TreeTable` tells it (§B.3/§B.4).
  const register = selection.registerGrid;
  useEffect(() => {
    register(order, TICK_COLUMNS);
  }, [register, order]);

  /**
   * Which row the keyboard is on — **an id, not an index.** The list is rebuilt by every
   * keystroke in the search box, and an index kept across that rebuild names a different row
   * each time; an id either still exists or honestly does not.
   */
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeIndex = activeId === null ? -1 : order.indexOf(activeId);
  const list = useListWindow(rows.length, ROW_HEIGHT, activeIndex);
  // One prefix per mounted rail, so two rails on one screen cannot mint the same row id.
  const rowDomId = useId();

  // Keep the active row on screen. Arithmetic rather than `scrollIntoView`, because the row a
  // key just moved to is routinely one the window has not drawn yet.
  useEffect(() => {
    scrollRowIntoView(list.box, activeIndex, ROW_HEIGHT);
  }, [list.box, activeIndex]);

  if (facet === null) return null;

  // A slice the facet cannot offer: a branch somebody else removed, a person whose list this
  // caller may not read. The list still answers about them, so they stay sliced here — and
  // they are cleared together, because the rail cannot tell one unnameable id from another
  // well enough to offer them separately.
  const nameable = sliced.filter((id) => has(facet.options, id));
  const unknown = nameable.length !== sliced.length;

  /** The ticked rows, in drawn order — what *Show only these* slices to. */
  const ticked = selectable ? selection.rows : [];

  /** Open what is shut, shut what is open — the rail's one fold writer. */
  const toggle = (id: string): void => {
    const next = new Set(shut);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setCollapsed(next);
  };

  /**
   * The label's gesture: **slice**. Plain replaces, `additive` (`Cmd`/`Ctrl`) adds or removes
   * this one row. Pressing a row is also arriving at it, so the keyboard's row moves too.
   */
  const slice = (id: string, additive: boolean): void => {
    setActiveId(id);
    if (additive) {
      onSlice(
        slicedSet.has(id) ? sliced.filter((one) => one !== id) : [...sliced, id],
      );
      return;
    }
    // Plain replaces — except on the one row that is already the whole slice, where replacing
    // would be a no-op and clearing is the only "never mind" the rail itself has.
    onSlice(sliced.length === 1 && slicedSet.has(id) ? [] : [id]);
  };

  /**
   * The select column's gesture: **tick**, through the table's own `pressRow` (§B.8). Never a
   * slice — nothing here reaches `onSlice`.
   */
  const tick = (id: string, shift: boolean): void => {
    setActiveId(id);
    selection.pressRow(id, { shift, additive: false });
  };

  return (
    <aside
      className={[styles.rail, inDrawer ? styles.railInDrawer : ""].filter(Boolean).join(" ")}
      aria-label={title}
    >
      {/* The drawer draws the name in its own header, and two headings one above the other
          saying the same word is a panel that looks broken. */}
      {inDrawer ? null : (
        <header className={styles.head}>
          <h3 className={styles.title}>{title}</h3>
        </header>
      )}

      {/* **The axis picker** (§6). One facet means there is nothing to pick between, and a
          dropdown with one entry is a control that looks like a choice and is not. */}
      {facets.length > 1 ? (
        <div className={styles.facet}>
          <Select
            label={t("slicer.facet")}
            size="sm"
            value={facet.id}
            onChange={(event) => {
              setSearch("");
              // The ticks and their anchor name rows of the facet being left; carried into the
              // next facet they would be a count of nothing on screen.
              selection.clear();
              onFacetChange(event.target.value);
            }}
          >
            {facets.map((one) => (
              <option key={one.id} value={one.id}>
                {one.label}
              </option>
            ))}
          </Select>
        </div>
      ) : null}

      {/* The search box and the option rows are the only part a facet with nothing to offer
          drops. An unnameable slice is not about the options — it is the state of the slice,
          and a rail that hid it would show a narrowed list with neither a name for the
          narrowing nor a way out of it (§G's `403` case). */}
      {facet.options.length === 0 ? null : (
        <div className={styles.searchRow}>
          <Search
            size={14}
            strokeWidth={1.5}
            className={styles.searchIcon}
            aria-hidden
          />
          <input
            type="search"
            className={styles.search}
            value={search}
            aria-label={t("slicer.search")}
            placeholder={t("slicer.search")}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            /* Down out of the search box and into the results, which is what a search box
               above a list has always done — otherwise narrowing to one row still costs a
               reach for the mouse. Down lands on the first match, up on the last. */
            onKeyDown={(event) => {
              if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
              if (rows.length === 0) return;
              event.preventDefault();
              list.box?.focus();
              setActiveId(
                (event.key === "ArrowDown" ? order[0] : order[rows.length - 1]) ??
                  null,
              );
            }}
          />
        </div>
      )}

      {/* Pinned above the scrolling list rather than inside it. It is not an option — it is
          the state of the slice — and a row that is sometimes there and sometimes not would
          otherwise have to be counted into the window's arithmetic for no gain. */}
      {unknown ? (
        <button
          type="button"
          className={[styles.option, styles.optionSliced, styles.pinned].join(" ")}
          aria-pressed
          onClick={() => {
            onSlice(nameable);
          }}
        >
          {facet.unknownLabel}
        </button>
      ) : null}

      {/**
       * The rows: a flat tree, windowed, one tab stop.
       *
       * `role="tree"` with `aria-activedescendant` rather than a list of buttons, because a
       * windowed list cannot be tabbed through — the row you would tab to is not in the
       * document until it is scrolled to. `aria-level` carries the nesting the indentation
       * draws, and `aria-setsize`/`aria-posinset` carry the two facts a windowed list takes
       * away from assistive technology: how many rows there are, and where this one sits among
       * them (`KLQ-20260820-005`'s last panel criterion).
       */}
      <ul
        ref={list.ref}
        className={styles.list}
        role="tree"
        aria-label={facet.label}
        aria-multiselectable={selectable}
        /* No tab stop while there is nothing in it: an empty tree that swallows a Tab is a
           dead end, and the sentence saying why it is empty is below it. */
        tabIndex={rows.length === 0 ? -1 : 0}
        aria-activedescendant={
          activeIndex < 0 ? undefined : `${rowDomId}-${String(activeIndex)}`
        }
        // A drag down the select column ends wherever the button comes up, the table's rule.
        onMouseUp={selection.endDrag}
        onMouseLeave={selection.endDrag}
        onKeyDown={(event) => {
          // The table's `Escape`: the ticks go, the slice stays. Checked before the empty
          // guard, because a search that hid every row has not made the ticks go away.
          if (event.key === "Escape" && ticked.length > 0) {
            event.preventDefault();
            selection.clear();
            return;
          }
          if (rows.length === 0) return;
          const step: Record<string, number | undefined> = {
            ArrowDown: 1,
            ArrowUp: -1,
          };
          const delta = step[event.key];
          if (delta !== undefined) {
            event.preventDefault();
            // From nothing, the first arrow lands on the top row rather than the second —
            // `-1 + 1` is 0 going down, and going up from nothing is the top too.
            const next = activeIndex < 0 ? 0 : activeIndex + delta;
            setActiveId(order[Math.max(0, Math.min(rows.length - 1, next))] ?? null);
            return;
          }
          /**
           * **The fold, from the keyboard** — the WAI-ARIA tree binding, and not optional:
           * the twisty is `tabbable={false}` (one tab stop for the whole rail), so without
           * these two keys a branch is visibly shut and no key opens it. That is exactly the
           * failure `e2e/bulk-actions-keyboard.spec.ts` found in the tree table.
           *
           * `ArrowRight` opens a shut branch and steps into an open one; `ArrowLeft` shuts an
           * open branch and steps out to the parent of everything else. "Out" is the nearest
           * row above with a smaller depth, read off the rows as drawn rather than off the
           * tree — the drawn list is what the arrows walk, so it is what "the parent" means.
           */
          if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
            if (activeId === null) return;
            const row = rows[activeIndex];
            if (row === undefined) return;
            event.preventDefault();
            if (event.key === "ArrowRight") {
              if (row.expandable && !row.open) toggle(row.id);
              else if (row.open) setActiveId(order[activeIndex + 1] ?? activeId);
              return;
            }
            if (row.expandable && row.open) {
              toggle(row.id);
              return;
            }
            for (let index = activeIndex - 1; index >= 0; index -= 1) {
              const above = rows[index];
              if (above !== undefined && above.depth < row.depth) {
                setActiveId(above.id);
                return;
              }
            }
            return;
          }
          if (event.key === "Home" || event.key === "End") {
            event.preventDefault();
            setActiveId(
              (event.key === "Home" ? order[0] : order[rows.length - 1]) ?? null,
            );
            return;
          }
          if (activeId === null) return;
          // `Enter` is the label, `Space` is the tick — see the module note. Without a select
          // column there is nothing for `Space` to tick, so it slices as it always did.
          if (event.key === "Enter" || (event.key === " " && !selectable)) {
            event.preventDefault();
            slice(activeId, event.metaKey || event.ctrlKey);
            return;
          }
          if (event.key === " ") {
            event.preventDefault();
            tick(activeId, event.shiftKey);
          }
        }}
      >
        {list.window.above > 0 ? (
          <li
            role="none"
            aria-hidden
            style={{ height: `${String(list.window.above)}px` }}
          />
        ) : null}
        {rows.slice(list.window.first, list.window.last).map((row, offset) => {
          const index = list.window.first + offset;
          const isSliced = slicedSet.has(row.id);
          const isTicked = selectable && selection.isRowSelected(row.id);
          return (
            <li
              key={row.id}
              id={`${rowDomId}-${String(index)}`}
              role="treeitem"
              aria-level={row.depth + 1}
              {...(row.expandable ? { "aria-expanded": row.open } : {})}
              {...(selectable ? { "aria-selected": isTicked } : {})}
              aria-current={isSliced ? true : undefined}
              aria-setsize={rows.length}
              aria-posinset={index + 1}
              className={[
                styles.option,
                styles.row,
                isSliced ? styles.optionSliced : "",
                isTicked ? styles.optionTicked : "",
                index === activeIndex ? styles.optionActive : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {/* **The select column.** A painted cell, not a form checkbox — the table's
                  `.tick`, for the table's reasons (TWIN: `.tick` in
                  `tree-table/TreeTable.module.css`). `tabIndex={-1}` for the twisty's reason:
                  the whole list is one tab stop, and `Space` is what the keyboard gets.

                  The press is on `mousedown`, as the table's is, so a drag can start from it —
                  and it keeps DOM focus on the list rather than on this button, because the
                  list is where `Space` and the arrows are heard. */}
              {selectable ? (
                <button
                  type="button"
                  role="checkbox"
                  tabIndex={-1}
                  aria-checked={isTicked}
                  aria-label={t("slicer.tick", { name: row.label })}
                  className={styles.tick}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    list.box?.focus();
                    tick(row.id, event.shiftKey);
                  }}
                  onMouseEnter={(event) => {
                    if (event.buttons !== 1) {
                      selection.endDrag();
                      return;
                    }
                    selection.drag({ rowId: row.id, colKey: SELECT_COLUMN_KEY });
                  }}
                />
              ) : null}
              {/* **The label: the slice.** The indent is this box's own padding, so the select
                  column stays one straight edge at the far left however deep the row is — the
                  table draws its tick column outside the indent for the same reason. */}
              <span
                className={styles.optionBody}
                style={{
                  paddingInlineStart: `${String((selectable ? 2 : 8) + row.depth * 14)}px`,
                }}
                // `Shift` + click otherwise drags a text selection across the rail.
                onMouseDown={(event) => {
                  if (event.shiftKey) event.preventDefault();
                }}
                onClick={(event) => {
                  // One chord, two platforms: `Cmd` on a Mac, `Ctrl` everywhere else. Taking
                  // either everywhere costs nothing and spares a keyboard-shortcut table.
                  slice(row.id, event.metaKey || event.ctrlKey);
                }}
              >
                {/* `tabbable={false}`: the whole list is one tab stop under
                    `aria-activedescendant`, so a twisty with a stop of its own would put two
                    thousand of them in the tab order. `ArrowRight` / `ArrowLeft` above is what
                    the tree pattern gives a keyboard instead. The twisty stops its own click,
                    so opening a branch never slices to it. */}
                {row.expandable ? (
                  <Twisty
                    tabbable={false}
                    open={row.open}
                    // `treeTable.*`, not a pair of the rail's own: it is one shared control
                    // doing one act, and two keys reading *Expand {name}* is two translations
                    // that can drift apart while saying the same thing.
                    label={t(
                      row.open ? "treeTable.collapse" : "treeTable.expand",
                      { name: row.label },
                    )}
                    onToggle={() => {
                      toggle(row.id);
                    }}
                  />
                ) : (
                  <TwistySpacer />
                )}
                <span className={styles.optionLabel}>{row.label}</span>
              </span>
            </li>
          );
        })}
        {list.window.below > 0 ? (
          <li
            role="none"
            aria-hidden
            style={{ height: `${String(list.window.below)}px` }}
          />
        ) : null}
      </ul>

      {/* Two different absences, said differently: the facet has nothing at all (the
          consumer's sentence, because it is about the data), or the needle matched none of
          what it does have (the control's own sentence, from `app`). */}
      {facet.options.length === 0 ? (
        <p className={styles.muted}>{facet.empty}</p>
      ) : options.length === 0 ? (
        <p className={styles.muted}>{t("slicer.noMatches")}</p>
      ) : null}

      {/* **The selection's strip** — only while something is ticked, and under the rows it
          counts. The one action is the one the rail knows: turn the ticks into the slice.
          `role="status"` on the count is the polite announcement §I.3 gives the table's bar. */}
      {ticked.length > 0 ? (
        <div className={styles.ticks} role="group" aria-label={t("slicer.ticked", { count: ticked.length })}>
          <span className={styles.ticksCount} role="status">
            {t("slicer.ticked", { count: ticked.length })}
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              onSlice(ticked);
              // The draft has become the slice. Leaving the ticks on would draw the same set
              // twice in two marks and invite a second press that does nothing.
              selection.clear();
            }}
          >
            {t("slicer.sliceTicked")}
          </Button>
          <Button size="sm" variant="tertiary" onClick={selection.clear}>
            {t("slicer.clearTicks")}
          </Button>
        </div>
      ) : null}
    </aside>
  );
}
